import { Component, OnInit, OnDestroy } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-health-insurances',
  templateUrl: './health-insurances.component.html',
  styleUrls: ['./health-insurances.component.scss']
})
export class HealthInsurancesComponent implements OnInit, OnDestroy {
  result: any = { items: [], totalCount: 0, page: 1, pageSize: 20 };
  loading = false;
  professionalId = 0;
  filter: any = { page: 1, pageSize: 20, search: '', isActive: true };

  showModal = false;
  saving = false;
  editingId: number | null = null;
  form: any = {
    name: '', cnpj: '', contactEmail: '', contactPhone: '', paymentDays: ''
  };

  private searchInput$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private profState: ProfessionalStateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$.pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(term => { this.filter.search = term; this.filter.page = 1; this.load(); })
    );

    this.subs.add(
      this.profState.selected$.subscribe(p => {
        if (p) { this.professionalId = p.id; this.load(); }
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  load(): void {
    this.loading = true;
    this.api.getHealthInsurances(this.professionalId, this.filter).subscribe({
      next: r => { this.result = r; this.loading = false; },
      error: () => this.loading = false
    });
  }

  onSearch(term: string): void { this.searchInput$.next(term); }
  onPageChange(page: number): void { this.filter.page = page; this.load(); }
  onSizeChange(size: number): void { this.filter.pageSize = size; this.filter.page = 1; this.load(); }

  openModal(healthInsurance?: any): void {
    this.editingId = healthInsurance?.id ?? null;
    this.form = healthInsurance ? {
      ...healthInsurance,
      cnpj:         healthInsurance.cnpj         ? this.formatCnpj(healthInsurance.cnpj)           : '',
      contactPhone: healthInsurance.contactPhone ? this.formatPhone(healthInsurance.contactPhone)   : ''
    } : { name: '', cnpj: '', contactEmail: '', contactPhone: '', paymentDays: '' };
    this.showModal = true;
  }

  save(): void {
    if (!this.form.name?.trim()) {
      this.notification.warning('Atenção', 'Nome é obrigatório');
      return;
    }

    this.saving = true;
    const op = this.editingId
      ? this.api.updateHealthInsurance(this.professionalId, this.editingId, this.form)
      : this.api.createHealthInsurance(this.professionalId, this.form);

    op.subscribe({
      next: () => {
        this.saving = false;
        this.showModal = false;
        this.notification.success('Sucesso!', `Convênio ${this.editingId ? 'atualizado' : 'cadastrado'} com sucesso`);
        this.load();
      },
      error: () => {
        this.saving = false;
        this.notification.error('Erro', 'Erro ao salvar convênio');
      }
    });
  }

  delete(healthInsurance: any): void {
    this.notification.confirm(
      'Confirmar Exclusão',
      `Tem certeza que deseja excluir o convênio ${healthInsurance.name}?`,
      () => {
        this.api.deleteHealthInsurance(this.professionalId, healthInsurance.id).subscribe({
          next: () => {
            this.notification.success('Sucesso!', 'Convênio excluído com sucesso');
            this.load();
          },
          error: () => this.notification.error('Erro', 'Erro ao excluir convênio')
        });
      }
    );
  }

  fmtDate(d?: string): string {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }

  // ── Máscaras ────────────────────────────────────────────────────
  formatPhone(value: string): string {
    const v = value.replace(/\D/g, '').substring(0, 11);
    if (v.length === 0) return '';
    if (v.length <= 2)  return `(${v}`;
    if (v.length <= 6)  return `(${v.substring(0, 2)}) ${v.substring(2)}`;
    if (v.length <= 10) return `(${v.substring(0, 2)}) ${v.substring(2, 6)}-${v.substring(6)}`;
    return `(${v.substring(0, 2)}) ${v.substring(2, 7)}-${v.substring(7)}`;
  }

  onContactPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatPhone(input.value);
    this.form.contactPhone = formatted;
    input.value = formatted;
  }

  formatCnpj(value: string): string {
    const v = value.replace(/\D/g, '').substring(0, 14);
    if (v.length > 12) return v.substring(0, 2) + '.' + v.substring(2, 5) + '.' + v.substring(5, 8) + '/' + v.substring(8, 12) + '-' + v.substring(12);
    if (v.length > 8)  return v.substring(0, 2) + '.' + v.substring(2, 5) + '.' + v.substring(5, 8) + '/' + v.substring(8);
    if (v.length > 5)  return v.substring(0, 2) + '.' + v.substring(2, 5) + '.' + v.substring(5);
    if (v.length > 2)  return v.substring(0, 2) + '.' + v.substring(2);
    return v;
  }

  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatCnpj(input.value);
    this.form.cnpj = formatted;
    input.value = formatted;
  }
}
